import axios from 'axios';
import { Student, Batch, Class } from '@/types';
import { verifyToken } from './jwtUtils';

// Create an axios instance with default config
const api = axios.create({
  baseURL: '/api', // Use relative URL for the Nginx proxy
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // Add timeout
  timeout: 10000,
  // Add CORS settings
  withCredentials: false, // Don't send cookies with cross-origin requests
});

// Add a request interceptor to include the auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    const tenantCode = localStorage.getItem('tenantCode');
    const normalizedToken = token?.startsWith('Bearer ')
      ? token.substring('Bearer '.length)
      : token;
    const tokenPayload = normalizedToken ? verifyToken(normalizedToken) : null;

    if (token) {
      console.log('API: Adding token to request:', config.url);

      // Ensure the token is properly formatted
      // The backend expects "Bearer <token>"
      let formattedToken = token;

      // Remove any existing "Bearer " prefix to avoid duplication
      if (token.startsWith('Bearer ')) {
        formattedToken = token;
      } else {
        formattedToken = `Bearer ${token}`;
      }

      // Set the Authorization header
      config.headers.Authorization = formattedToken;

      // Log the token format for debugging
      console.log('API: Token format:', {
        length: token.length,
        parts: token.split('.').length,
        startsWithBearer: token.startsWith('Bearer ')
      });

      // Log the Authorization header for debugging (truncated for security)
      const truncatedToken = formattedToken.length > 20
        ? `${formattedToken.substring(0, 20)}...`
        : formattedToken;
      console.log('API: Authorization header:', truncatedToken);
    } else {
      console.warn('API: No token found for request:', config.url);

      // For auth endpoints, we don't need a token
      if (config.url?.includes('/auth/')) {
        console.log('API: Auth endpoint, no token needed');
      } else {
        console.warn('API: Non-auth endpoint without token');
        // We'll handle this in the response interceptor
      }
    }

    // Add tenant header from local storage first, then JWT payload as fallback
    const tenantHeader = tenantCode || tokenPayload?.tenantId || tokenPayload?.collegeId;
    if (tenantHeader) {
      config.headers['X-Tenant-ID'] = tenantHeader;
      console.log('API: Added X-Tenant-ID header:', tenantHeader);
    } else {
      console.warn('API: No tenant identifier available for request:', config.url);
    }

    // Add user ID header for admin operations
    if (!config.url?.includes('/auth/')) {
      const userId = localStorage.getItem('userId') || tokenPayload?.userId || tokenPayload?.sub;
      if (userId) {
        config.headers['X-User-ID'] = userId;
        console.log('API: Added X-User-ID header:', userId);
      } else {
        console.warn('API: No user ID available for request:', config.url);
      }
    }

    return config;
  },
  (error) => {
    console.error('API: Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle authentication errors
api.interceptors.response.use(
  (response) => {
    // Log successful responses
    console.log(`API Success: ${response.config.method?.toUpperCase()} ${response.config.url}`, response.status);
    return response;
  },
  async (error) => {
    // Log all API errors
    console.error(`API Error: ${error.config?.method?.toUpperCase()} ${error.config?.url}`, {
      status: error.response?.status,
      data: error.response?.data
    });

    // Handle 401 Unauthorized errors
    if (error.response && error.response.status === 401) {
      console.error('API: Authentication error (401):', error.config.url);

      // Check if this is an auth endpoint
      if (error.config.url?.includes('/auth/')) {
        console.log('API: Auth endpoint failed, not redirecting');
        return Promise.reject(error);
      }

      // Check if we're already on the login page to avoid redirect loops
      if (window.location.pathname !== '/login') {
        console.log('API: Non-auth endpoint failed with 401, clearing token and redirecting to login');

        // Clear the old token
        localStorage.removeItem('token');
        localStorage.removeItem('tenantCode');

        // Redirect to login page
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Student API endpoints
export const studentApi = {
  // Get all students
  getAll: async (batchId?: string): Promise<Student[]> => {
    const params = batchId ? { batchId } : {};
    const response = await api.get('/admin/students', { params });

    // Handle paginated response from Spring Boot
    if (response.data && response.data.content) {
      return response.data.content;
    }

    // Handle direct array response (fallback)
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get all students with pagination
  getAllPaginated: async (batchId?: string, page = 0, size = 20): Promise<any> => {
    const params: any = { page, size };
    if (batchId) {
      params.batchId = batchId;
    }
    const response = await api.get('/admin/students', { params });
    return response.data;
  },

  // Get a student by ID
  getById: async (id: string): Promise<Student> => {
    const response = await api.get(`/admin/students/${id}`);
    return response.data;
  },

  // Create a new student
  create: async (student: Omit<Student, 'id'>): Promise<Student> => {
    const response = await api.post('/admin/students', student);
    return response.data;
  },

  // Update a student
  update: async (id: string, student: Partial<Student>): Promise<Student> => {
    const response = await api.put(`/admin/students/${id}`, student);
    return response.data;
  },

  // Delete a student
  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/students/${id}`);
  },

  // Deactivate a student
  deactivate: async (id: string): Promise<Student> => {
    const response = await api.patch(`/admin/students/${id}/deactivate`);
    return response.data;
  },

  // Bulk delete students
  bulkDelete: async (ids: string[]): Promise<void> => {
    await api.post('/admin/students/bulk-delete', { studentIds: ids });
  },

  // Import students from file
  import: async (file: File, batchId: string): Promise<Student[]> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('batchId', batchId);

    const response = await api.post('/admin/students/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Resend welcome email to individual student
  resendWelcomeEmail: async (studentId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/admin/students/${studentId}/resend-welcome-email`);
    return response.data;
  },

  // Reset password for individual student
  resetPassword: async (studentId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/admin/students/${studentId}/reset-password`);
    return response.data;
  },

  // Archive a student
  archive: async (id: string): Promise<Student> => {
    const response = await api.post(`/admin/students/${id}/archive`);
    return response.data;
  },

  // Get archived students
  getArchived: async (page = 0, size = 20): Promise<any> => {
    const response = await api.get('/admin/students/archived', {
      params: { page, size }
    });
    return response.data;
  },

  // Restore an archived student
  restore: async (id: string): Promise<Student> => {
    const response = await api.post(`/admin/students/${id}/restore`);
    return response.data;
  },

  // Revoke student access (for fee non-payment)
  revokeAccess: async (id: string, reason?: string): Promise<Student> => {
    const response = await api.post(`/admin/students/${id}/revoke-access`, { reason });
    return response.data;
  },

  // Restore student access (after fee payment)
  restoreAccess: async (id: string): Promise<Student> => {
    const response = await api.post(`/admin/students/${id}/restore-access`);
    return response.data;
  },

  // Deactivate student account (when student leaves college)
  deactivateAccount: async (id: string, reason?: string): Promise<Student> => {
    const response = await api.post(`/admin/students/${id}/deactivate-account`, { reason });
    return response.data;
  },

  // Reactivate student account (when student returns)
  reactivateAccount: async (id: string): Promise<Student> => {
    const response = await api.post(`/admin/students/${id}/reactivate-account`);
    return response.data;
  }
};




// Class API endpoints
export const classApi = {
  // Get all classes
  getAll: async (batchId?: string): Promise<Class[]> => {
    const endpoint = batchId
      ? `/admin/classes/batch/${batchId}`
      : '/admin/classes';
    const response = await api.get(endpoint);

    // Handle paginated response from Spring Boot
    if (response.data && response.data.content) {
      return response.data.content;
    }

    // Handle direct array response (fallback)
    return Array.isArray(response.data) ? response.data : [];
  },

  // Get a class by ID
  getById: async (id: string): Promise<Class> => {
    const response = await api.get(`/admin/classes/${id}`);
    return response.data;
  },

  // Create a new class
  create: async (classData: Omit<Class, 'id'>): Promise<Class> => {
    const response = await api.post('/admin/classes', classData);
    return response.data;
  },

  // Update a class
  update: async (id: string, classData: Partial<Class>): Promise<Class> => {
    const response = await api.put(`/admin/classes/${id}`, classData);
    return response.data;
  },

  // Delete a class
  delete: async (id: string): Promise<void> => {
    await api.delete(`/admin/classes/${id}`);
  },

  // Update students in a class
  updateStudents: async (id: string, studentIds: string[]): Promise<Class> => {
    const response = await api.put(`/admin/classes/${id}/students`, studentIds);
    return response.data;
  },

  // Add a student to a class
  addStudent: async (classId: string, studentId: string): Promise<void> => {
    await api.post(`/admin/classes/${classId}/students/${studentId}`);
  },

  // Remove a student from a class
  removeStudent: async (classId: string, studentId: string): Promise<void> => {
    await api.delete(`/admin/classes/${classId}/students/${studentId}`);
  },

  // Check if welcome emails can be sent for a class
  canSendWelcomeEmails: async (classId: string): Promise<{ canSend: boolean; message: string }> => {
    const response = await api.get(`/admin/classes/${classId}/can-send-welcome-emails`);
    return response.data;
  },

  // Send bulk welcome emails to all students in a class
  sendBulkWelcomeEmails: async (classId: string): Promise<{ success: boolean; message: string }> => {
    const response = await api.post(`/admin/classes/${classId}/send-welcome-emails`);
    return response.data;
  },

  // Archive a class
  archive: async (id: string): Promise<Class> => {
    const response = await api.post(`/admin/classes/${id}/archive`);
    return response.data;
  },

  // Get archived classes
  getArchived: async (page = 0, size = 20): Promise<any> => {
    const response = await api.get('/admin/classes/archived', {
      params: { page, size }
    });
    return response.data;
  },

  // Restore an archived class
  restore: async (id: string): Promise<Class> => {
    const response = await api.post(`/admin/classes/${id}/restore`);
    return response.data;
  }
};

// Test Management API endpoints
export const testApi = {
  // Get all tests with pagination and filtering (configurations)
  getAll: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: string;
    status?: string;
    subject?: string;
    search?: string;
  }) => {
    const response = await api.get('/admin/tests', { params });
    return response.data;
  },

  // Get test by ID (configuration)
  getById: async (id: string) => {
    try {
      const response = await api.get(`/admin/tests/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching test by ID:', error);
      throw error;
    }
  },

  // Get standard test configurations
  getStandardConfigurations: async () => {
    try {
      const response = await api.get('/admin/tests/configurations/standard');
      return response.data;
    } catch (error) {
      console.error('Error fetching standard configurations:', error);
      throw error;
    }
  },

  // Create test from standard configuration
  createFromConfiguration: async (configId: string, testData: {
    title: string;
    description?: string;
  }) => {
    try {
      const response = await api.post(`/admin/tests/from-configuration/${configId}`, testData);
      return response.data;
    } catch (error: any) {
      console.error('Error creating test from configuration:', error);

      // Debug: Log the error structure to understand what we're getting
      console.log('API Error Details:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        message: error?.message
      });

      // Re-throw the error with preserved structure
      throw error;
    }
  },

  // Create custom test
  createCustom: async (testData: {
    title: string;
    description?: string;
    subjectIds: string[];
    subjectNames: string[];
    topicIds?: string[];
    topicNames?: string[];
    duration: number;
    totalQuestions: number;
    difficultyDistribution: {
      easyPercentage: number;
      mediumPercentage: number;
      hardPercentage: number;
    };
    difficultyDistributionEnabled?: boolean;
    kitNumbers?: string[];
    tagFilters?: string[];
    selectedQuestionIds?: string[];
    autoGenerateRemainingQuestions?: boolean;
    markingScheme?: {
      correctMarks: number;
      incorrectMarks: number;
      unansweredMarks: number;
      negativeMarkingEnabled: boolean;
      maxMarks: number;
    };
  }) => {
    try {
      const response = await api.post('/admin/tests/custom', testData);
      return response.data;
    } catch (error) {
      console.error('Error creating custom test:', error);
      throw error;
    }
  },

  // Get actual tests (not configurations)
  getActualTests: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: string;
  }) => {
    try {
      const response = await api.get('/admin/tests/actual', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching actual tests:', error);
      throw error;
    }
  },

  // Get actual test by ID
  getActualTestById: async (id: string) => {
    try {
      const response = await api.get(`/admin/tests/actual/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching actual test by ID:', error);
      throw error;
    }
  },

  // Update test
  updateTest: async (id: string, testData: {
    title?: string;
    description?: string;
    duration?: number;
    regenerateQuestions?: boolean;
  }) => {
    try {
      const response = await api.put(`/admin/tests/actual/${id}`, testData);
      return response.data;
    } catch (error) {
      console.error('Error updating test:', error);
      throw error;
    }
  },

  // Publish test
  publishTest: async (id: string, publishData?: {
    scheduledDate?: string;
  }) => {
    try {
      const response = await api.post(`/admin/tests/actual/${id}/publish`, publishData || {});
      return response.data;
    } catch (error) {
      console.error('Error publishing test:', error);
      throw error;
    }
  },

  // Delete test
  deleteTest: async (id: string) => {
    try {
      await api.delete(`/admin/tests/actual/${id}`);
    } catch (error) {
      console.error('Error deleting test:', error);
      throw error;
    }
  },

  // Clone test
  cloneTest: async (id: string, cloneData: {
    title: string;
    description?: string;
    includeAssignments?: boolean;
  }) => {
    try {
      const response = await api.post(`/admin/tests/actual/${id}/clone`, cloneData);
      return response.data;
    } catch (error) {
      console.error('Error cloning test:', error);
      throw error;
    }
  },

  // Get test questions for preview
  getTestQuestions: async (id: string) => {
    try {
      const response = await api.get(`/admin/tests/actual/${id}/questions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching test questions:', error);
      throw error;
    }
  },

  // Validate question availability for configuration
  validateQuestionAvailability: async (configId: string) => {
    try {
      const response = await api.post(`/admin/tests/configurations/${configId}/validate`);
      return response.data;
    } catch (error) {
      console.error('Error validating question availability:', error);
      throw error;
    }
  },

  // Get test statistics for dashboard
  getStats: async () => {
    try {
      const response = await api.get('/admin/tests/stats');
      return response.data;
    } catch (error) {
      console.error('Error fetching test stats:', error);
      throw error;
    }
  },

  // Create new test
  create: async (testData: any) => {
    try {
      const response = await api.post('/admin/tests', testData);
      return response.data;
    } catch (error) {
      console.error('Error creating test:', error);
      throw error;
    }
  },

  // Update test
  update: async (id: string, testData: any) => {
    try {
      const response = await api.put(`/admin/tests/${id}`, testData);
      return response.data;
    } catch (error) {
      console.error('Error updating test:', error);
      throw error;
    }
  },

  // Delete test
  delete: async (id: string) => {
    try {
      await api.delete(`/admin/tests/${id}`);
    } catch (error) {
      console.error('Error deleting test:', error);
      throw error;
    }
  },

  // Publish test
  publish: async (id: string) => {
    try {
      const response = await api.post(`/admin/tests/${id}/publish`);
      return response.data;
    } catch (error) {
      console.error('Error publishing test:', error);
      throw error;
    }
  },

  // Assign test
  assign: async (id: string, assignmentData: {
    classIds?: string[];
    studentIds?: string[];
    deadline?: string;
  }) => {
    try {
      // Transform deadline to start/end times for the backend
      const now = new Date();
      const startTime = now.toISOString();
      const endTime = assignmentData.deadline || new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString();

      const payload = {
        classIds: assignmentData.classIds,
        studentIds: assignmentData.studentIds,
        startTime,
        endTime,
        allowLateSubmission: false,
        shuffleQuestions: false,
        shuffleOptions: false,
        maxAttempts: 1,
        instructions: 'Complete this test within the given time limit.',
        adminNotes: 'Test assigned via admin portal'
      };

      const response = await api.post(`/admin/tests/actual/${id}/assign`, payload);
      return response.data;
    } catch (error) {
      console.error('Error assigning test:', error);
      throw error;
    }
  },

  // Get test assignments
  getAssignments: async (id: string) => {
    const response = await api.get(`/admin/tests/actual/${id}/assignments`);
    return response.data;
  },

  // Get test statistics
  getStatistics: async (id: string) => {
    const response = await api.get(`/admin/tests/${id}/statistics`);
    return response.data;
  },



  // Publish scheduled test
  publishScheduledTest: async (id: string) => {
    try {
      const response = await api.post(`/admin/tests/actual/${id}/publish-scheduled`);
      return response.data;
    } catch (error) {
      console.error('Error publishing scheduled test:', error);
      throw error;
    }
  },

  // Validate test modification
  validateTestModification: async (id: string) => {
    try {
      const response = await api.get(`/admin/tests/actual/${id}/modification-validation`);
      return response.data;
    } catch (error) {
      console.error('Error validating test modification:', error);
      throw error;
    }
  },

  // Schedule test only (Step 3)
  scheduleTest: async (id: string, data: {
    startTime?: string;
    endTime?: string;
    isOnDemandTest?: boolean;
    onDemandTestStartDate?: string;
    onDemandTestEndDate?: string;
  }) => {
    try {
      const payload = {
        startTime: data.startTime,
        endTime: data.endTime,
        isOnDemandTest: data.isOnDemandTest || false,
        onDemandTestStartDate: data.onDemandTestStartDate,
        onDemandTestEndDate: data.onDemandTestEndDate
      };

      const response = await api.post(`/admin/tests/actual/${id}/schedule`, payload);
      return response.data;
    } catch (error) {
      console.error('Error scheduling test:', error);
      throw error;
    }
  },

  // Assign and publish test (Step 4)
  assignAndPublish: async (id: string, data: {
    classIds?: string[];
    studentIds?: string[];
    allowLateSubmission?: boolean;
    shuffleQuestions?: boolean;
    shuffleOptions?: boolean;
    maxAttempts?: number;
    instructions?: string;
    adminNotes?: string;
  }) => {
    try {
      const payload = {
        classIds: data.classIds || [],
        studentIds: data.studentIds || [],
        allowLateSubmission: data.allowLateSubmission || false,
        shuffleQuestions: data.shuffleQuestions || false,
        shuffleOptions: data.shuffleOptions || false,
        maxAttempts: data.maxAttempts || 1,
        instructions: data.instructions || "Complete this test within the given time limit.",
        adminNotes: data.adminNotes || "Test assigned via admin portal"
      };

      const response = await api.post(`/admin/tests/actual/${id}/assign-publish`, payload);
      return response.data;
    } catch (error) {
      console.error('Error assigning and publishing test:', error);
      throw error;
    }
  },

  // Schedule and assign test (atomic operation - for backward compatibility)
  scheduleAndAssign: async (id: string, data: {
    startTime?: string;
    endTime?: string;
    isOnDemandTest?: boolean;
    onDemandTestStartDate?: string;
    onDemandTestEndDate?: string;
    classIds?: string[];
    studentIds?: string[];
  }) => {
    try {
      // Use the new atomic endpoint that handles schedule, assign, and publish in one transaction
      const payload = {
        startTime: data.startTime,
        endTime: data.endTime,
        onDemandTest: data.isOnDemandTest || false,
        onDemandTestStartDate: data.onDemandTestStartDate,
        onDemandTestEndDate: data.onDemandTestEndDate,
        classIds: data.classIds,
        studentIds: data.studentIds,
        allowLateSubmission: false,
        shuffleQuestions: false,
        shuffleOptions: false,
        maxAttempts: 1,
        instructions: 'Complete this test within the given time limit.',
        adminNotes: 'Test scheduled and assigned via admin portal'
      };

      const response = await api.post(`/admin/tests/actual/${id}/schedule-assign-publish`, payload);
      return response.data;
    } catch (error) {
      console.error('Error scheduling and assigning test:', error);
      throw error;
    }
  },

  // Finalize test (for draft creation)
  finalizeTest: async (id: string) => {
    try {
      const response = await api.post(`/admin/tests/${id}/finalize`);
      return response.data;
    } catch (error) {
      console.error('Error finalizing test:', error);
      throw error;
    }
  }
};

// Subject API endpoints
export const subjectApi = {
  // Get all subjects
  getAll: async () => {
    const response = await api.get('/admin/subjects');
    return response.data;
  },

  // Get active subjects
  getActive: async () => {
    const response = await api.get('/admin/subjects/active');
    return response.data;
  }
};

// Test Configuration API endpoints
export const testConfigurationApi = {
  // Get all test configurations
  getAll: async (params?: {
    page?: number;
    size?: number;
    category?: string;
    examType?: string;
    search?: string;
    activeOnly?: boolean;
  }) => {
    const response = await api.get('/admin/test-configurations', { params });
    return response.data;
  },

  // Get test configuration by ID
  getById: async (id: string) => {
    try {
      const response = await api.get(`/admin/test-configurations/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching test configuration by ID:', error);
      throw error;
    }
  },

  // Get standard test configurations
  getStandard: async () => {
    const response = await api.get('/admin/test-configurations/standard');
    return response.data;
  },

  // Get custom test configurations
  getCustom: async () => {
    const response = await api.get('/admin/test-configurations/custom');
    return response.data;
  },

  // Get available exam types
  getExamTypes: async () => {
    const response = await api.get('/admin/test-configurations/exam-types');
    return response.data;
  },

  // Get configurations by exam type
  getByExamType: async (examType: string) => {
    const response = await api.get(`/admin/test-configurations/exam-type/${examType}`);
    return response.data;
  },

  // Create new test configuration
  create: async (configData: any) => {
    try {
      const response = await api.post('/admin/test-configurations', configData);
      return response.data;
    } catch (error) {
      console.error('Error creating test configuration:', error);
      throw error;
    }
  },

  // Update test configuration
  update: async (id: string, configData: any) => {
    try {
      const response = await api.put(`/admin/test-configurations/${id}`, configData);
      return response.data;
    } catch (error) {
      console.error('Error updating test configuration:', error);
      throw error;
    }
  },

  // Delete test configuration
  delete: async (id: string) => {
    try {
      await api.delete(`/admin/test-configurations/${id}`);
    } catch (error) {
      console.error('Error deleting test configuration:', error);
      throw error;
    }
  },

  // Toggle configuration status
  toggleStatus: async (id: string, isActive: boolean) => {
    try {
      const response = await api.patch(`/admin/test-configurations/${id}/status`, null, {
        params: { isActive }
      });
      return response.data;
    } catch (error) {
      console.error('Error toggling test configuration status:', error);
      throw error;
    }
  }
};

// Dashboard API endpoints
export const dashboardApi = {
  // Get dashboard statistics
  getStats: async () => {
    try {
      const [studentStats, classStats, testStats] = await Promise.all([
        api.get('/admin/students/stats'),
        api.get('/admin/classes/stats'),
        api.get('/admin/tests/stats')
      ]);

      return {
        totalStudents: studentStats.data.totalStudents || 0,
        totalClasses: classStats.data.totalClasses || 0,
        totalTests: testStats.data.totalTests || 0,
        upcomingTests: testStats.data.upcomingTests || 0
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // Return mock data as fallback
      return {
        totalStudents: 0,
        totalClasses: 0,
        totalTests: 0,
        upcomingTests: 0
      };
    }
  },

  // Get recent activities
  getRecentActivities: async () => {
    try {
      const response = await api.get('/admin/audit/recent-activities');
      return response.data;
    } catch (error) {
      console.error('Error fetching recent activities:', error);
      // Return mock data as fallback
      return [
        { id: 1, time: 'Today at 10:30 AM', description: 'New student John Doe was added to Computer Science class' },
        { id: 2, time: 'Yesterday at 2:15 PM', description: 'Physics Midterm test was published' },
        { id: 3, time: 'Yesterday at 11:45 AM', description: 'New class "Advanced Mathematics" was created' },
        { id: 4, time: '2 days ago', description: '5 students completed the Math Quiz' }
      ];
    }
  }
};

// Batch API endpoints (replaces Academic Year terminology)
export const batchApi = {
  // Get all batches
  getAll: async (): Promise<Batch[]> => {
    try {
      const response = await api.get('/admin/batches');
      console.log('Fetched batches:', response.data);

      // Handle paginated response from Spring Boot
      const data = response.data.content || response.data;

      // Check if data is an array
      if (!Array.isArray(data)) {
        console.error('Expected array or paginated response, got:', typeof data, data);
        throw new Error('Invalid response format: expected array of batches');
      }

      // Transform the response to match our frontend model
      const transformedData = data.map((batch: any) => ({
        ...batch,
        // Ensure isCurrent is properly set from the response
        isCurrent: batch.isCurrent || batch.current || false,
      }));

      return transformedData;
    } catch (error) {
      console.error('Error fetching batches:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response data:', error.response?.data);
        console.error('Response status:', error.response?.status);
      }
      throw error;
    }
  },

  // Get batch by ID
  getById: async (id: string): Promise<Batch> => {
    try {
      const response = await api.get(`/admin/batches/${id}`);
      console.log('Fetched batch by ID:', response.data);

      // Transform the response to match our frontend model
      const transformedData = {
        ...response.data,
        // Ensure isCurrent is properly set from the response
        isCurrent: response.data.isCurrent || response.data.current || false,
      };

      return transformedData;
    } catch (error) {
      console.error('Error fetching batch by ID:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response data:', error.response?.data);
        console.error('Response status:', error.response?.status);
      }
      throw error;
    }
  },

  // Get the current batch - DEPRECATED: Current batch is now user-specific and stored in localStorage
  getCurrent: async (): Promise<Batch> => {
    console.warn('batchApi.getCurrent() is deprecated. Current batch is now user-specific and managed by BatchContext.');
    try {
      const batches = await batchApi.getAll();
      // Return the first batch as fallback since isCurrent is no longer used
      if (batches.length > 0) {
        return batches[0];
      }
      throw new Error('No batches found');
    } catch (error) {
      console.error('Error fetching current batch:', error);
      throw error;
    }
  },

  // Create a new batch
  create: async (batch: Omit<Batch, 'id'>): Promise<Batch> => {
    try {
      // Handle the field name difference between frontend and backend
      const payload = {
        ...batch,
      };

      console.log('Creating batch:', payload);
      const response = await api.post('/admin/batches', payload);
      console.log('Batch created successfully:', response.data);

      // Transform the response to match our frontend model if needed
      const transformedData = {
        ...response.data,
        // Ensure isCurrent is properly set from the response
        isCurrent: response.data.isCurrent || response.data.current || false,
      };

      return transformedData;
    } catch (error) {
      console.error('Error creating batch:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response data:', error.response?.data);
        console.error('Response status:', error.response?.status);
      }
      throw error;
    }
  },

  // Update a batch
  update: async (id: string, batch: Partial<Batch>): Promise<Batch> => {
    try {
      // Handle the field name difference between frontend and backend
      const payload = {
        ...batch,
      };

      console.log('Updating batch:', id, payload);
      const response = await api.put(`/admin/batches/${id}`, payload);
      console.log('Batch updated successfully:', response.data);

      // Transform the response to match our frontend model if needed
      const transformedData = {
        ...response.data,
        // Ensure isCurrent is properly set from the response
        isCurrent: response.data.isCurrent || response.data.current || false,
      };

      return transformedData;
    } catch (error) {
      console.error('Error updating batch:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response data:', error.response?.data);
        console.error('Response status:', error.response?.status);
      }
      throw error;
    }
  },

  // Delete a batch
  delete: async (id: string): Promise<void> => {
    try {
      await api.delete(`/admin/batches/${id}`);
    } catch (error) {
      console.error('Error deleting batch:', error);
      if (axios.isAxiosError(error)) {
        console.error('Response data:', error.response?.data);
        console.error('Response status:', error.response?.status);
      }
      throw error;
    }
  },

  // Set current batch - DEPRECATED: Current batch is now user-specific and stored in localStorage
  setCurrent: async (id: string): Promise<Batch> => {
    console.warn('batchApi.setCurrent() is deprecated. Current batch is now user-specific and managed by BatchContext.');
    try {
      // Just return the batch by ID since we no longer update database-level current batch
      return await batchApi.getById(id);
    } catch (error) {
      console.error('Error getting batch for setCurrent:', error);
      throw error;
    }
  },

  // Archive a batch
  archive: async (id: string): Promise<Batch> => {
    const response = await api.post(`/admin/batches/${id}/archive`);
    return response.data;
  },

  // Get archived batches
  getArchived: async (page = 0, size = 20): Promise<any> => {
    const response = await api.get('/admin/batches/archived', {
      params: { page, size }
    });
    return response.data;
  },

  // Restore an archived batch
  restore: async (id: string): Promise<Batch> => {
    const response = await api.post(`/admin/batches/${id}/restore`);
    return response.data;
  }
};

// Kit API endpoints (Updated for new kit-tag architecture)
export const kitApi = {
  // Get kit names for type-ahead and upload workflows
  getKitNames: async (search?: string): Promise<string[]> => {
    try {
      const params = search ? { search } : {};
      const response = await api.get(search ? '/admin/kits/search' : '/admin/kits', { params });
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching kit names:', error);
      throw error;
    }
  },

  // Get all kits with details (name, type, tags, question count)
  getAllKits: async (params?: {
    page?: number;
    size?: number;
    search?: string;
    type?: string;
    status?: string;
  }) => {
    try {
      const response = await api.get('/admin/kits', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching kits:', error);
      throw error;
    }
  },

  // Search kits for type-ahead (returns kit objects with id, name, type, etc.)
  searchKits: async (search?: string) => {
    try {
      const params = search ? { search } : {};
      const response = await api.get('/admin/kits/search', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching kits:', error);
      throw error;
    }
  },

  // Get kit by ID
  getById: async (id: string) => {
    try {
      const response = await api.get(`/admin/kits/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching kit by ID:', error);
      throw error;
    }
  },

  // Create new kit
  create: async (kitData: {
    name: string;
    description?: string;
    type: string;
    tags: string[];
  }) => {
    try {
      const response = await api.post('/admin/kits', kitData);
      return response.data;
    } catch (error) {
      console.error('Error creating kit:', error);
      throw error;
    }
  },

  // Update kit
  update: async (id: string, kitData: {
    name?: string;
    description?: string;
    type?: string;
    tags?: string[];
  }) => {
    try {
      const response = await api.put(`/admin/kits/${id}`, kitData);
      return response.data;
    } catch (error) {
      console.error('Error updating kit:', error);
      throw error;
    }
  },

  // Delete kit
  delete: async (id: string) => {
    try {
      const response = await api.delete(`/admin/kits/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting kit:', error);
      throw error;
    }
  },

  // Update kit tags
  updateTags: async (id: string, tagData: { tags: string[] }) => {
    try {
      const response = await api.put(`/admin/kits/${id}/tags`, tagData);
      return response.data;
    } catch (error) {
      console.error('Error updating kit tags:', error);
      throw error;
    }
  },

  // Get questions for a kit (based on kit's tags)
  getQuestions: async (id: string, params?: {
    page?: number;
    size?: number;
  }) => {
    try {
      const response = await api.get(`/admin/kits/${id}/questions`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching kit questions:', error);
      throw error;
    }
  }
};

// Question API endpoints
export const questionApi = {
  // Get all questions with pagination and filtering
  getAll: async (params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: string;
    subject?: string;
    topic?: string;
    difficulty?: string;
    search?: string;
    tags?: string;
  }) => {
    try {
      const response = await api.get('/admin/questions', { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching questions:', error);
      throw error;
    }
  },

  // Get question by ID
  getById: async (id: string) => {
    try {
      const response = await api.get(`/admin/questions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching question by ID:', error);
      throw error;
    }
  },

  // Create new question
  create: async (questionData: any) => {
    try {
      const response = await api.post('/admin/questions', questionData);
      return response.data;
    } catch (error) {
      console.error('Error creating question:', error);
      throw error;
    }
  },

  // Update question
  update: async (id: string, questionData: any) => {
    try {
      const response = await api.put(`/admin/questions/${id}`, questionData);
      return response.data;
    } catch (error) {
      console.error('Error updating question:', error);
      throw error;
    }
  },

  // Delete question
  delete: async (id: string) => {
    try {
      await api.delete(`/admin/questions/${id}`);
    } catch (error) {
      console.error('Error deleting question:', error);
      throw error;
    }
  },

  // Get all distinct subjects from questions
  getSubjects: async () => {
    try {
      const response = await api.get('/admin/questions/subjects');
      return response.data;
    } catch (error) {
      console.error('Error getting subjects:', error);
      throw error;
    }
  },

  // Search subjects for type-ahead
  searchSubjects: async (search?: string) => {
    try {
      const params = search ? { search } : {};
      const response = await api.get('/admin/questions/subjects/search', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching subjects:', error);
      throw error;
    }
  },

  // Search topics for type-ahead (filtered by subject)
  searchTopics: async (subject?: string, search?: string) => {
    try {
      const params: any = {};
      if (subject) params.subject = subject;
      if (search) params.search = search;
      const response = await api.get('/admin/questions/topics/search', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching topics:', error);
      throw error;
    }
  },

  // Search exam types for type-ahead
  searchExamTypes: async (search?: string) => {
    try {
      const params = search ? { search } : {};
      const response = await api.get('/admin/questions/exam-types/search', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching exam types:', error);
      throw error;
    }
  },

  // Search subtopics for type-ahead (filtered by topic)
  searchSubtopics: async (topic?: string, search?: string) => {
    try {
      const params: any = {};
      if (topic) params.topic = topic;
      if (search) params.search = search;
      const response = await api.get('/admin/questions/subtopics/search', { params });
      return response.data;
    } catch (error) {
      console.error('Error searching subtopics:', error);
      throw error;
    }
  },

  // Get exam types for dropdown
  getExamTypes: async () => {
    try {
      const response = await api.get('/admin/questions/exam-types');
      return response.data;
    } catch (error) {
      console.error('Error getting exam types:', error);
      throw error;
    }
  },

  // Bulk upload questions from CSV/Excel with optional kit name binding
  bulkUpload: async (file: File, options?: { tenantCode?: string; kitName?: string }) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (options?.tenantCode) {
        formData.append('tenantCode', options.tenantCode);
      }
      if (options?.kitName) {
        formData.append('kitName', options.kitName);
      }

      const response = await api.post('/admin/questions/bulk-upload', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error bulk uploading questions:', error);
      throw error;
    }
  }
};

// Export the api instance for other services
export default api;
